/**
 * SDK 配置选项
 */
export interface MetricTrackerConfig {
  /** 应用编码（必填） */
  appCode: string;

  /** 指标服务基础 URL（必填） */
  baseUrl: string;

  /** 文档节点对象（可选，默认为 window.document） */
  document?: Document;

  /** 是否启用调试模式（默认 false） */
  debug?: boolean;

  /** 上报间隔时间（毫秒，默认 1000，最小 1000） */
  reportInterval?: number;

  /** 事件队列最大长度（默认 100） */
  maxQueueSize?: number;

  /**
   * 允许自动追踪的域名列表（可选）
   * 支持通配符，如：['*.laeni.cn', 'example.com']
   * 默认只允许当前域名
   */
  allowedDomains?: string[];

  /**
   * 路由模式（可选）
   * - 'auto': 自动检测（如果 hash 中包含斜杠则认为是 hash 路由，否则按 history 模式处理）
   * - 'history': history 模式（使用 pathname）
   * - 'hash': hash 模式（使用 hash 作为路径）
   * 默认：'auto'
   */
  routeMode?: 'auto' | 'history' | 'hash';
}

/**
 * 内部配置（合并用户配置和默认值后的完整配置）
 */
export interface InternalConfig extends Required<Omit<MetricTrackerConfig, 'document' | 'allowedDomains' | 'routeMode'>> {
  userId?: string;
  document: Document;
  allowedDomains: string[];
  routeMode: 'auto' | 'history' | 'hash';
}

/**
 * 创建内部配置
 */
export function createInternalConfig(userConfig: MetricTrackerConfig): InternalConfig {
  let allowedDomains = userConfig.allowedDomains ?? [window.location.hostname];
  if (!allowedDomains.includes(window.location.hostname)) {
    allowedDomains.push(window.location.hostname);
  }

  const config: InternalConfig = {
    appCode: userConfig.appCode,
    baseUrl: userConfig.baseUrl.replace(/\/$/, ''), // 移除末尾斜杠
    document: userConfig.document || window.document,
    debug: userConfig.debug ?? false,
    reportInterval: Math.max(userConfig.reportInterval ?? 1000, 1000), // 最小 1 秒
    maxQueueSize: userConfig.maxQueueSize ?? 2,
    allowedDomains: userConfig.allowedDomains ?? [window.location.hostname],
    routeMode: userConfig.routeMode ?? 'auto',
  };

  return config;
}
